import React, { useEffect, useRef, useCallback } from "react";

// Configurazione particelle
const config = {
  particleCount: 400,
  minParticleSize: 0.8,
  maxParticleSize: 1.8,
  speed: 1.8,
  connectionDistance: 0,
  particleColors: ["rgba(255, 255, 255,", "rgba(240, 240, 255,"],
};

// Classe Particella
class Particle {
  constructor(canvasWidth, canvasHeight) {
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.reset();
    this.vx = (Math.random() - 0.5) * config.speed;
    this.vy = (Math.random() - 0.5) * config.speed;
    this.size = Math.random() * (config.maxParticleSize - config.minParticleSize) + config.minParticleSize;
    this.baseColor = config.particleColors[Math.floor(Math.random() * config.particleColors.length)];
    this.opacity = Math.random() * 0.7 + 0.3;
  }

  reset() {
    this.x = Math.random() * this.canvasWidth;
    this.y = Math.random() * this.canvasHeight;
  }

  update() {
    this.x += this.vx;
    this.y += this.vy;

    if (this.x < 0) this.x = this.canvasWidth;
    else if (this.x > this.canvasWidth) this.x = 0;

    if (this.y < 0) this.y = this.canvasHeight;
    else if (this.y > this.canvasHeight) this.y = 0;
  }

  draw(ctx) {
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    ctx.fillStyle = `${this.baseColor}${this.opacity})`;
    ctx.fill();
  }
}

const StarCanvas = ({ className }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);
  const lastFrameTimeRef = useRef(0);
  const fpsInterval = 1000 / 30;
  const resizeTimeoutRef = useRef(null);

  const animate = useCallback(
    (timestamp) => {
      if (!lastFrameTimeRef.current) lastFrameTimeRef.current = timestamp;
      const elapsed = timestamp - lastFrameTimeRef.current;

      if (elapsed > fpsInterval) {
        lastFrameTimeRef.current = timestamp - (elapsed % fpsInterval);

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        particlesRef.current.forEach((particle) => {
          particle.update();
          particle.draw(ctx);
        });
      }

      animationRef.current = requestAnimationFrame(animate);
    },
    [fpsInterval]
  );

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    canvas.style.width = `${window.innerWidth}px`;
    canvas.style.height = `${window.innerHeight}px`;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    if (particlesRef.current.length !== config.particleCount || particlesRef.current.length === 0) {
      particlesRef.current = Array.from(
        { length: config.particleCount },
        () => new Particle(window.innerWidth, window.innerHeight)
      );
    } else {
      particlesRef.current.forEach((p) => {
        if (p instanceof Particle) {
          p.canvasWidth = window.innerWidth;
          p.canvasHeight = window.innerHeight;
          p.reset();
        }
      });
    }
  }, []);

  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    resizeTimeoutRef.current = setTimeout(() => {
      initCanvas();
    }, 200);
  }, [initCanvas]);

  useEffect(() => {
    initCanvas();
    animationRef.current = requestAnimationFrame(animate);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
    };
  }, [animate, handleResize, initCanvas]);

  return <canvas ref={canvasRef} className={className} style={{ zIndex: 1 }} />;
};

export default StarCanvas;
